import {
  API_KEY_EXPIRY_DAYS,
  type ApiKeyCreated,
  type ApiKeyInfo,
  type ApiKeyScope,
  WEBHOOK_EVENTS,
  type WebhookEndpointCreated,
  type WebhookEndpointInfo,
  type WebhookEvent,
} from "@kontaktfix/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  Field,
  Input,
  Select,
  Skeleton,
} from "@kontaktfix/ui";
import {
  ArrowSquareOutIcon,
  KeyIcon,
  PaperPlaneTiltIcon,
  PlusIcon,
  WebhooksLogoIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDate, formatRelative } from "@/lib/i18n";
import { useLists, useMe } from "@/lib/queries";
import { CopyValue } from "@/pages/connect-own-app";

const KEYS = ["api-keys"] as const;
const ENDPOINTS = ["webhook-endpoints"] as const;
const DOCS_URL = {
  de: "https://kontaktfix.ch/api-dokumentation",
  en: "https://kontaktfix.ch/en/api-documentation",
};

/** Settings > API (issue #32): API keys and webhook endpoints, administrators only. */
export function ApiTab() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const docs = i18n.language.startsWith("de") ? DOCS_URL.de : DOCS_URL.en;
  const docsLink = (
    <Button size="sm" variant="secondary" asChild>
      <a href={docs} target="_blank" rel="noreferrer">
        <ArrowSquareOutIcon />
        {t("apiAccess.docs")}
      </a>
    </Button>
  );
  if (me?.role !== "admin") {
    return (
      <Card>
        <CardHeader title={t("apiAccess.title")} description={t("apiAccess.adminOnly")} actions={docsLink} />
      </Card>
    );
  }
  return (
    <div className="grid gap-6">
      <ApiKeysCard docsLink={docsLink} />
      <WebhooksCard />
    </div>
  );
}

function ApiKeysCard({ docsLink }: { docsLink: React.ReactNode }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: keys, isPending } = useQuery({
    queryKey: KEYS,
    queryFn: () => api.get<ApiKeyInfo[]>("/api/api-keys"),
  });
  const { data: lists } = useLists();
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKeyInfo | null>(null);
  const listName = (id: string) => lists?.find((l) => l.id === id)?.name ?? "?";

  const revoke = async () => {
    if (!revoking) return;
    try {
      await api.delete(`/api/api-keys/${revoking.id}`);
      toast.success(t("apiAccess.keys.revoked"));
      await client.invalidateQueries({ queryKey: KEYS });
    } catch (err) {
      toastError(err);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("apiAccess.keys.title")}
        description={t("apiAccess.keys.description")}
        actions={
          <div className="flex flex-wrap gap-2">
            {docsLink}
            <Button size="sm" onClick={() => setCreating(true)}>
              <PlusIcon />
              {t("apiAccess.keys.create")}
            </Button>
          </div>
        }
      />
      {isPending ? (
        <Skeleton className="m-5 h-16" />
      ) : !keys?.length ? (
        <p className="px-5 py-4 text-[13px] text-fg-muted">{t("apiAccess.keys.empty")}</p>
      ) : (
        <ul className="divide-y divide-border">
          {keys.map((k) => {
            const expired = k.expiresAt && new Date(k.expiresAt) <= new Date();
            return (
              <li key={k.id} className="flex flex-wrap items-center gap-4 px-5 py-3">
                <KeyIcon className="size-5 shrink-0 text-fg-muted" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium">
                    {k.name}
                    <Badge tone={k.scope === "write" ? "accent" : "neutral"}>
                      {t(`apiAccess.scope.${k.scope}`)}
                    </Badge>
                    {expired && <Badge tone="danger">{t("apiAccess.keys.expired")}</Badge>}
                  </div>
                  <p className="text-[12.5px] text-fg-muted">
                    <span className="font-mono">{k.prefix}...</span>
                    {" · "}
                    {k.listIds ? k.listIds.map(listName).join(", ") : t("apiAccess.keys.allLists")}
                    {" · "}
                    {t("apiAccess.keys.meta", {
                      created: formatDate(k.createdAt),
                      by: k.createdBy ?? t("activity.system"),
                      used: k.lastUsedAt ? formatRelative(k.lastUsedAt) : t("common.never"),
                      expires: k.expiresAt ? formatDate(k.expiresAt) : t("apiAccess.keys.noExpiry"),
                    })}
                  </p>
                </div>
                <Button size="sm" variant="danger-ghost" onClick={() => setRevoking(k)}>
                  {t("apiAccess.keys.revoke")}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <CreateKeyDialog open={creating} onClose={() => setCreating(false)} />
      <Dialog
        open={Boolean(revoking)}
        onOpenChange={() => setRevoking(null)}
        title={t("apiAccess.keys.revokeTitle", { name: revoking?.name })}
        description={t("apiAccess.keys.revokeText")}
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={revoke}>
              {t("apiAccess.keys.revoke")}
            </Button>
          </>
        }
      />
    </Card>
  );
}

function CreateKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: lists } = useLists();
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ApiKeyScope>("read");
  const [limited, setLimited] = useState(false);
  const [listIds, setListIds] = useState<string[]>([]);
  const [expiry, setExpiry] = useState("365");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);

  const close = () => {
    onClose();
    setCreated(null);
    setName("");
    setScope("read");
    setLimited(false);
    setListIds([]);
    setExpiry("365");
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      setCreated(
        await api.post<ApiKeyCreated>("/api/api-keys", {
          name,
          scope,
          listIds: limited ? listIds : null,
          expiresInDays: expiry === "never" ? null : Number(expiry),
        }),
      );
      await client.invalidateQueries({ queryKey: KEYS });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Dialog
        open={open}
        onOpenChange={close}
        title={t("apiAccess.keys.createdTitle")}
        description={t("apiAccess.keys.createdText")}
        footer={<Button onClick={close}>{t("common.close")}</Button>}
      >
        <CopyValue value={created.key} />
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("apiAccess.keys.create")}
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button
            variant="primary"
            type="submit"
            form="api-key-form"
            loading={busy}
            disabled={!name.trim() || (limited && !listIds.length)}
          >
            {t("apiAccess.keys.create")}
          </Button>
        </>
      }
    >
      <form id="api-key-form" onSubmit={submit} className="grid gap-4">
        <Field label={t("apiAccess.keys.name")} hint={t("apiAccess.keys.nameHint")} htmlFor="api-key-name">
          <Input
            id="api-key-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />
        </Field>
        <Field
          label={t("apiAccess.keys.access")}
          htmlFor="api-key-scope"
          hint={t(`apiAccess.scopeHint.${scope}`)}
        >
          <Select id="api-key-scope" value={scope} onChange={(e) => setScope(e.target.value as ApiKeyScope)}>
            <option value="read">{t("apiAccess.scope.read")}</option>
            <option value="write">{t("apiAccess.scope.write")}</option>
          </Select>
        </Field>
        <Field label={t("apiAccess.keys.lists")} htmlFor="api-key-lists">
          <Select
            id="api-key-lists"
            value={limited ? "some" : "all"}
            onChange={(e) => setLimited(e.target.value === "some")}
          >
            <option value="all">{t("apiAccess.keys.allLists")}</option>
            <option value="some">{t("apiAccess.keys.someLists")}</option>
          </Select>
        </Field>
        {limited && (
          <div className="grid max-h-48 gap-2 overflow-y-auto rounded-md border border-border p-3">
            {lists?.map((l) => (
              // biome-ignore lint/a11y/noLabelWithoutControl: the checkbox inside is the control
              <label key={l.id} className="flex items-center gap-2 text-[13px]">
                <Checkbox
                  checked={listIds.includes(l.id)}
                  onCheckedChange={(on) =>
                    setListIds((ids) => (on ? [...ids, l.id] : ids.filter((id) => id !== l.id)))
                  }
                  label={l.name}
                />
                {l.name}
              </label>
            ))}
            <p className="text-[12px] text-fg-subtle">{t("apiAccess.keys.someListsHint")}</p>
          </div>
        )}
        <Field label={t("apiAccess.keys.expiry")} htmlFor="api-key-expiry">
          <Select id="api-key-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
            {API_KEY_EXPIRY_DAYS.map((d) => (
              <option key={d} value={String(d)}>
                {t("apiAccess.keys.days", { count: d })}
              </option>
            ))}
            <option value="never">{t("apiAccess.keys.noExpiry")}</option>
          </Select>
        </Field>
      </form>
    </Dialog>
  );
}

function WebhooksCard() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: endpoints, isPending } = useQuery({
    queryKey: ENDPOINTS,
    queryFn: () => api.get<WebhookEndpointInfo[]>("/api/webhook-endpoints"),
    refetchInterval: 30_000,
  });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<WebhookEndpointInfo | null>(null);

  const test = async (id: string) => {
    try {
      await api.post(`/api/webhook-endpoints/${id}/test`);
      toast.success(t("apiAccess.webhooks.testQueued"));
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async () => {
    if (!removing) return;
    try {
      await api.delete(`/api/webhook-endpoints/${removing.id}`);
      toast.success(t("apiAccess.webhooks.removed"));
      await client.invalidateQueries({ queryKey: ENDPOINTS });
    } catch (err) {
      toastError(err);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("apiAccess.webhooks.title")}
        description={t("apiAccess.webhooks.description")}
        actions={
          <Button size="sm" onClick={() => setAdding(true)}>
            <PlusIcon />
            {t("apiAccess.webhooks.add")}
          </Button>
        }
      />
      {isPending ? (
        <Skeleton className="m-5 h-16" />
      ) : !endpoints?.length ? (
        <p className="px-5 py-4 text-[13px] text-fg-muted">{t("apiAccess.webhooks.empty")}</p>
      ) : (
        <ul className="divide-y divide-border">
          {endpoints.map((e) => {
            const ok = e.lastStatus !== null && !e.lastError;
            return (
              <li key={e.id} className="flex flex-wrap items-center gap-4 px-5 py-3">
                <WebhooksLogoIcon className="size-5 shrink-0 text-fg-muted" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-[12.5px]">{e.url}</div>
                  <p className="text-[12.5px] text-fg-muted">
                    {t("apiAccess.webhooks.events", { count: e.events.length })}
                    {" · "}
                    {e.lastDeliveryAt
                      ? t("apiAccess.webhooks.last", { when: formatRelative(e.lastDeliveryAt) })
                      : t("apiAccess.webhooks.never")}
                    {e.pending > 0 && ` · ${t("apiAccess.webhooks.pending", { count: e.pending })}`}
                  </p>
                </div>
                {e.lastStatus !== null && (
                  <Badge tone={ok ? "success" : "danger"}>
                    {ok ? t("apiAccess.webhooks.ok") : (e.lastError ?? t("apiAccess.webhooks.failed"))}
                  </Badge>
                )}
                <Button size="sm" variant="secondary" onClick={() => test(e.id)}>
                  <PaperPlaneTiltIcon />
                  {t("apiAccess.webhooks.test")}
                </Button>
                <Button size="sm" variant="danger-ghost" onClick={() => setRemoving(e)}>
                  {t("apiAccess.webhooks.remove")}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <AddEndpointDialog open={adding} onClose={() => setAdding(false)} />
      <Dialog
        open={Boolean(removing)}
        onOpenChange={() => setRemoving(null)}
        title={t("apiAccess.webhooks.removeTitle")}
        description={t("apiAccess.webhooks.removeText", { url: removing?.url })}
        footer={
          <>
            <Button onClick={() => setRemoving(null)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={remove}>
              {t("apiAccess.webhooks.remove")}
            </Button>
          </>
        }
      />
    </Card>
  );
}

function AddEndpointDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>([...WEBHOOK_EVENTS]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<WebhookEndpointCreated | null>(null);
  const httpsOk = /^https:\/\/[^/]+/i.test(url.trim());

  const close = () => {
    onClose();
    setCreated(null);
    setUrl("");
    setEvents([...WEBHOOK_EVENTS]);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      setCreated(
        await api.post<WebhookEndpointCreated>("/api/webhook-endpoints", { url: url.trim(), events }),
      );
      await client.invalidateQueries({ queryKey: ENDPOINTS });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Dialog
        open={open}
        onOpenChange={close}
        title={t("apiAccess.webhooks.createdTitle")}
        description={t("apiAccess.webhooks.createdText")}
        footer={<Button onClick={close}>{t("common.close")}</Button>}
      >
        <CopyValue value={created.secret} />
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("apiAccess.webhooks.add")}
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button
            variant="primary"
            type="submit"
            form="webhook-form"
            loading={busy}
            disabled={!httpsOk || !events.length}
          >
            {t("apiAccess.webhooks.add")}
          </Button>
        </>
      }
    >
      <form id="webhook-form" onSubmit={submit} className="grid gap-4">
        <Field
          label={t("apiAccess.webhooks.url")}
          hint={t("apiAccess.webhooks.urlHint")}
          error={url.trim() && !httpsOk ? t("apiAccess.webhooks.httpsRequired") : null}
          htmlFor="webhook-url"
        >
          <Input
            id="webhook-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            autoFocus
          />
        </Field>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-[12.5px] font-medium">{t("apiAccess.webhooks.eventsLabel")}</legend>
          {WEBHOOK_EVENTS.map((ev) => (
            // biome-ignore lint/a11y/noLabelWithoutControl: the checkbox inside is the control
            <label key={ev} className="flex items-start gap-2 text-[13px]">
              <Checkbox
                checked={events.includes(ev)}
                onCheckedChange={(on) =>
                  setEvents((list) => (on ? [...list, ev] : list.filter((x) => x !== ev)))
                }
                label={ev}
              />
              <span>
                <span className="font-mono text-[12.5px]">{ev}</span>
                <span className="block text-[12px] text-fg-muted">
                  {t(`apiAccess.eventText.${ev.replace(".", "_")}`)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      </form>
    </Dialog>
  );
}
